import { Injectable, inject } from '@angular/core';
import {
  CoreRouterActionGoBack,
  CoreRouterActionNavigateForward,
  CoreRouterActions
} from '@bepatient/core';
import {
  EDocsActionDeleteFileSuccess,
  EDocsActionDeleteFolderSuccess,
  EDocsActionDownloadFileSuccess,
  EDocsActionLoadEDocs,
  EDocsActions,
  EDocsActionSaveFileSuccess,
  EDocsActionSaveFolderSuccess,
  EDocsActionTypes,
  EDocsActionUpdateFileSuccess,
  EDocsActionUpdateFolderSuccess
} from '@bepatient/edocs-store';
import { ToastController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable()
export class EDocsViewEffects {
  private actions$ = inject(Actions);
  private toastController = inject(ToastController);
  private translateService = inject(TranslateService);

  goToDocument$: Observable<CoreRouterActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(EDocsActionTypes.SELECT_FILE, EDocsActionTypes.CREATE_FILE),
      map(
        () =>
          new CoreRouterActionNavigateForward({
            url: 'document'
          })
      )
    )
  );

  goToFolder$: Observable<CoreRouterActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(EDocsActionTypes.SELECT_FOLDER, EDocsActionTypes.CREATE_FOLDER),
      map(
        () =>
          new CoreRouterActionNavigateForward({
            url: 'folder'
          })
      )
    )
  );

  saveFileSuccess$: Observable<CoreRouterActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(EDocsActionTypes.SAVE_FILE_SUCCESS),
      map((action: EDocsActionSaveFileSuccess) => action.payload),
      tap(file =>
        this.toast(
          this.translateService.instant('EDOCUMENTS.TOASTS.SAVE_SUCCESS', {
            item: file.title
          })
        )
      ),
      map(() => new CoreRouterActionGoBack())
    )
  );

  saveFolderSuccess$: Observable<CoreRouterActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(EDocsActionTypes.SAVE_FOLDER_SUCCESS),
      map((action: EDocsActionSaveFolderSuccess) => action.payload),
      tap(folder =>
        this.toast(
          this.translateService.instant('EDOCUMENTS.TOASTS.SAVE_SUCCESS', {
            item: folder.title
          })
        )
      ),
      map(() => new CoreRouterActionGoBack())
    )
  );

  updateFileSuccess$: Observable<CoreRouterActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(EDocsActionTypes.UPDATE_FILE_SUCCESS),
      map((action: EDocsActionUpdateFileSuccess) => action.payload),
      tap(file =>
        this.toast(
          this.translateService.instant('EDOCUMENTS.TOASTS.UPDATE_SUCCESS', {
            item: file.title
          })
        )
      ),
      map(() => new CoreRouterActionGoBack())
    )
  );

  updateFolderSuccess$: Observable<CoreRouterActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(EDocsActionTypes.UPDATE_FOLDER_SUCCESS),
      map((action: EDocsActionUpdateFolderSuccess) => action.payload),
      tap(file =>
        this.toast(
          this.translateService.instant('EDOCUMENTS.TOASTS.UPDATE_SUCCESS', {
            item: file.title
          })
        )
      ),
      map(() => new CoreRouterActionGoBack())
    )
  );

  deleteFileSuccess$: Observable<EDocsActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(EDocsActionTypes.DELETE_FILE_SUCCESS),
      map((action: EDocsActionDeleteFileSuccess) => action.payload),
      tap(file =>
        this.toast(
          this.translateService.instant('EDOCUMENTS.TOASTS.DELETE_SUCCESS', {
            item: file.title
          })
        )
      ),
      map(() => new EDocsActionLoadEDocs())
    )
  );

  deleteFolderSuccess$: Observable<EDocsActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(EDocsActionTypes.DELETE_FOLDER_SUCCESS),
      map((action: EDocsActionDeleteFolderSuccess) => action.payload),
      tap(folder =>
        this.toast(
          this.translateService.instant('EDOCUMENTS.TOASTS.DELETE_SUCCESS', {
            item: folder.title
          })
        )
      ),
      map(() => new EDocsActionLoadEDocs())
    )
  );

  downloadFileSuccess = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EDocsActionTypes.DOWNLOAD_FILE_SUCCESS),
        map((action: EDocsActionDownloadFileSuccess) => action.payload),
        tap(file =>
          this.toast(
            this.translateService.instant(
              'EDOCUMENTS.TOASTS.DOWNLOAD_SUCCESS',
              {
                item: file.title
              }
            )
          )
        )
      ),
    { dispatch: false }
  );

  failed = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          EDocsActionTypes.SAVE_FILE_FAILED,
          EDocsActionTypes.SAVE_FOLDER_FAILED,
          EDocsActionTypes.UPDATE_FILE_FAILED,
          EDocsActionTypes.UPDATE_FOLDER_FAILED,
          EDocsActionTypes.DELETE_FILE_FAILED,
          EDocsActionTypes.DELETE_FOLDER_FAILED,
          EDocsActionTypes.DOWNLOAD_FILE_FAILED
        ),
        tap(() => this.toast(this.translateService.instant('SAVE_FAILED')))
      ),
    { dispatch: false }
  );

  private toast(message: string): void {
    this.toastController
      .create({
        message,
        duration: 2000,
        position: 'top',
        cssClass: 'bepatient_dynamic_form_save_toast'
      })
      .then(toast => toast.present());
  }
}
